import StringUtils from "../utils/StringUtils.js";
import StorageService from "../services/StorageService.js";
import MapInteraction from "../utils/MapInteraction.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import MapExploration from "../utils/MapExploration.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import BaseGame from "./BaseGame.js";

/** Temps laissé au joueur pour lire la réponse révélée après une erreur, en ms. */
const REVEAL_DELAY = 1500;

export default class MapNerdHard extends BaseGame {
    /**
     * @param {string|null} continent Continent code (af/as/eu/na/sa/oc) to scope
     *   the game to, or null for the whole world. Infixed into the gameId
     *   (`mapnerdhard.af`) — see MapNerdClassic for the scope rationale.
     */
    constructor(continent = null) {
        const gameId = continent ? `mapnerdhard.${continent}` : "mapnerdhard";
        super(gameId, '.map-nerd-hard', {
            gameNameKey: "home.mapNerd",
            modeLabelKey: "home.hard",
            mode: "hard",
            shareEmoji: "🟥",
            slug: "map-nerd-hard"
        });
        if (!this.page) return;

        this.continent = continent;
        this.scopedPool = continent ? (geoNerdApp.easyCountriesByContinent[continent] || []) : geoNerdApp.easyCountries;

        this.mapContainer = this.page.querySelector(".map-container");
        this.map = this.mapContainer.querySelector("svg");
        this.countryToGuess = this.page.querySelector(".country-to-guess");
        this.countryToGuessName = this.countryToGuess.querySelector(".name");
        this.countryToGuessFlag = this.countryToGuess.querySelector(".flag-container");
        this.inputContainer = this.countryToGuess.querySelector(".input-container");
        this.answerInput = this.inputContainer.querySelector(".answer-input");
        this.countriesElements = this.map.querySelectorAll("path");

        this.mapInteraction = new MapInteraction(this.mapContainer, this.map);
        this.buildEvents();
        this.infiniteButton();
        this.init();

        this.isInfinite = false;
        this.selectionMode = true;
    }

    init() {
        this.initCountries(this.scopedPool);
        this.startClock();
        this.newProposal();
        this.reloadButton();
        this.initShare();
        this.updateHeartsUI();
        this.updateProgress();
    }

    initCountries(defaultList) {
        super.initCountries(defaultList);
        const scopeCodes = new Set(this.scopedPool.map(c => c.code));
        geoNerdApp.easyCountries.forEach(c => {
            const path = this.map.querySelector(`#${c.code}`);
            if (!path) return;
            if (scopeCodes.has(c.code)) {
                path.classList.add("onu");
                if (!this.countriesLeft.find(left => left.code === c.code)) {
                    path.classList.add("guessed");
                }
            } else {
                path.classList.add("out-of-scope");
            }
        });
    }

    buildEvents() {
        this.countriesElements.forEach(country => {
            country.addEventListener("click", () => {
                if (!this.mapInteraction.dragged && this.canGuess && this.selectionMode && country.classList.contains("onu") && !country.classList.contains("guessed")) {
                    this.handleMapClick(country.id);
                }
            });
        });
        this.answerInput.addEventListener("input", () => {
            if (this.countryToGuessFlag.innerHTML !== "") {
                this.countryToGuessFlag.innerHTML = "";
                this.countryToGuessFlag.classList.remove("show");
            }
        });
        this.answerInput.addEventListener("keypress", e => {
            if (e.key === "Enter") this.validateTyping(this.answerInput.value);
        });
    }

    handleMapClick(countryCode) {
        const countryData = geoNerdApp.easyCountries.find(c => c.code === countryCode);
        if (!countryData) return;
        if (this.rightAnswer) {
            const prevPath = this.map.querySelector(`#${this.rightAnswer.code}`);
            if (prevPath) prevPath.classList.remove("selected");
        }
        this.rightAnswer = countryData;
        this.countryToGuessFlag.innerHTML = "";
        this.countryToGuessFlag.classList.remove("show");
        this.countryToGuessName.innerHTML = "";
        this.inputContainer.classList.add("active");
        this.countryToGuess.classList.add("typing-mode");
        this.answerInput.focus();
        const path = this.map.querySelector(`#${countryCode}`);
        if (path) path.classList.add("selected");
    }

    validateTyping(answer) {
        if (!this.canGuess || !this.rightAnswer) return;
        // Valider à vide ne coûte rien : sans réponse il n'y a pas de faute à
        // sanctionner (même règle que Flag Nerd Hard et le Défi du jour hard).
        if (!answer || !answer.trim()) return;
        this.guessResult(StringUtils.matchesAnswer(answer, this.rightAnswer, geoNerdApp.easyCountries));
    }

    guessResult(isCorrect) {
        if (this.destroyed) return;
        this.canGuess = false;
        this.selectionMode = true;

        if (!isCorrect) {
            this.recordError({
                countryName: this.rightAnswer.name,
                correctCode: this.rightAnswer.code,
                guessedText: this.answerInput.value
            });
        }

        if (isCorrect) {
            SoundManager.correct();
            FeedbackEffects.confetti(this.countryToGuess);
            this.countryToGuess.classList.add("valid");
            this.countryToGuessFlag.innerHTML = `<div class="flag" style="background-image: url(${getFlagPath(this.rightAnswer.code)})"></div>`;
            this.countryToGuessFlag.classList.add("show");
            const path = this.map.querySelector(`#${this.rightAnswer.code}`);
            if (path) {
                path.classList.remove("selected");
                path.classList.add("guessed");
            }
            // Permanently record this country as "explored" (cross-scope, cross-language).
            MapExploration.record(this.rightAnswer.code);
            this.countriesLeft = this.countriesLeft.filter(elem => elem.code !== this.rightAnswer.code);
            StorageService.save(`${this.gameId}.current`, { found: true });
            this.updateStorage();
            this.updateProgress();

            // Correct: the flag replaces the input, so we can collapse the
            // typing UI immediately.
            this.inputContainer.classList.remove("active");
            this.countryToGuess.classList.remove("typing-mode");
            this.answerInput.value = "";
        } else {
            SoundManager.wrong();
            FeedbackEffects.shake(this.countryToGuess);
            this.countryToGuess.classList.add("invalid");
            const path = this.map.querySelector(`#${this.rightAnswer.code}`);
            if (path) {
                path.classList.remove("selected");
                path.classList.add("invalid-flash");
                setTimeout(() => path.classList.remove("invalid-flash"), 500);
            }
            // Le pays est déjà désigné (c'est le joueur qui l'a cliqué) : ce qui
            // manquait, c'était son nom. Sans lui, se tromper n'apprenait rien
            // jusqu'au récap de fin de partie.
            this.revealAnswerName();
        }

        // Une erreur laisse le temps de lire le nom révélé.
        const delay = isCorrect ? 600 : REVEAL_DELAY;

        if (this.isInfinite || isCorrect) {
            setTimeout(() => {
                this.newProposal();
                this.canGuess = true;
            }, delay);
        } else {
            this.updateLife(true);
            if (this.lifes > 0) {
                setTimeout(() => {
                    this.newProposal();
                    this.canGuess = true;
                }, delay);
            }
        }
    }

    /** Affiche le drapeau et le nom du pays que le joueur n'a pas su nommer. */
    revealAnswerName() {
        const lang = window.geoNerdApp.languageManager;
        this.countryToGuessFlag.innerHTML = `<div class="flag" style="background-image: url(${getFlagPath(this.rightAnswer.code)})"></div>`;
        this.countryToGuessFlag.classList.add("show");
        this.countryToGuessName.innerHTML = `${lang.t("game.answerWas")} <strong>${this.rightAnswer.name}</strong>`;
        this.inputContainer.classList.remove("active");
        this.countryToGuess.classList.remove("typing-mode");
    }

    newProposal() {
        if (this.destroyed) return;
        if (this.countriesLeft.length === 0) {
            this.finishGame();
            return;
        }
        gsap.to(this.countryToGuess, { opacity: 1 });
        this.countryToGuess.classList.remove("valid", "invalid", "typing-mode");
        this.inputContainer.classList.remove("active");
        this.answerInput.value = "";
        this.countryToGuessName.innerHTML = window.geoNerdApp.languageManager.t('game.chooseCountry');
        this.countryToGuessFlag.innerHTML = "";
        this.countryToGuessFlag.classList.remove("show");
        this.rightAnswer = null;
        this.selectionMode = true;
    }

    updateLife(decrease) {
        if (this.isInfinite) {
            this.updateProgress();
            return;
        }
        if (decrease) {
            this.lifes--;
            StorageService.save(`${this.gameId}.lifes`, this.lifes);
        }
        this.updateHeartsUI();
        if (this.lifes <= 0) {
            SoundManager.gameOver();
            this.lifes = 0;
            StorageService.save(`${this.gameId}.lifes`, 0);
            this.recordLoss();

            const correctPath = this.map.querySelector(`#${this.rightAnswer.code}`);
            if (correctPath) {
                correctPath.classList.add("correct-answer");
                this.mapInteraction.zoomToElement(correctPath, null, 2);
            }

            this.revealRecapAfterDelay(() => {
                this.renderEndGameStats(this.looseMessage);
                this.renderErrors(this.looseMessage);
                this.looseMessage.classList.add("show");
                this.page.querySelector(".progress-container").style.display = "none";
                if (correctPath) correctPath.classList.remove("correct-answer");
            });
        }
        this.updateProgress();
    }

    /**
     * Défère `reveal` de 2 s, le temps de voir où était le pays sur la carte
     * avant de passer au récap. Le bouton "Recommencer" disparaît dans l'instant :
     * laissé visible, il restait cliquable pendant ce délai et réinitialisait la
     * partie au lieu de montrer le récap. La sauvegarde (best/stats) a déjà eu
     * lieu au moment de cet appel.
     */
    revealRecapAfterDelay(reveal) {
        const restartBtn = this.page.querySelector(".btn-restart-ingame");
        if (restartBtn) restartBtn.style.display = "none";
        setTimeout(reveal, 2000);
    }

    infiniteButton() {
        const btn = this.page.querySelector(".btn.infinite"), quitBtn = this.page.querySelector(".btn.infinite-quit");
        if (btn) {
            btn.addEventListener("click", () => {
                this.isInfinite = true;
                this.looseMessage.classList.remove("show");
                this.page.querySelector(".life-remaining").style.display = "none";
                this.page.querySelector(".btn-restart-ingame").style.display = "none";
                this.page.querySelector(".progress-container").style.display = "flex";
                this.page.querySelector(".progress-container").style.pointerEvents = "all";
                this.page.querySelector(".infinite-actions").style.display = "flex";
                if (!this.page.querySelector(".infinite-label-added")) {
                    this.page.querySelector(".progress").insertAdjacentHTML("beforeend", `<span class="infinite-label-added">&nbsp;-&nbsp;${window.geoNerdApp.languageManager.t('game.infiniteMode')}</span>`);
                }
                this.newProposal();
                this.canGuess = true;
            });
        }
        if (quitBtn) {
            quitBtn.addEventListener("click", () => {
                window.location.href = window.geoNerdApp.languageManager.currentLang === "en" ? "/en/" : "/";
            });
        }
    }

    renderErrorItem(error) {
        return `
            <div class="error-item">
                <div class="error-flag correct">
                    <img src="${getFlagPath(error.correctCode)}" alt="${error.countryName}">
                </div>
                <div class="error-details">
                    <div class="error-country">${error.countryName}</div>
                    <div class="error-answer-text wrong">${error.guessedText}</div>
                </div>
            </div>
        `;
    }

    finishGame() {
        SoundManager.victory();
        this.mapInteraction.destroy();
        this.recordWin();
        // Le score reste affiché derrière le bouton de révélation (au lieu de
        // disparaître aussitôt) : sans ce rafraîchissement, "MEILLEUR" resterait
        // à l'ancienne valeur pendant toute cette attente.
        this.updateProgress();
        this.revealRecapAfterDelay(() => this.showWinMessage());
    }

    /**
     * Detach the game when the controller switches scope (see MapNerdClassic.teardown).
     */
    teardown() {
        this.destroyed = true;
        this.canGuess = false;
        if (this.mapInteraction) this.mapInteraction.destroy();
    }
}
